import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { LoadingSpinner } from '@/components/auth'
import { EmailAuthForm } from '@/components/auth/EmailAuthForm'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  LanguageSwitcher,
} from '@/components/ui'
import { useAuth } from '@/contexts/auth'
import { captureError } from '@/lib/monitoring'
import { consumePostLoginPath } from '@/lib/utils'

export function LoginPage() {
  const { t } = useTranslation()
  const { user, signInWithGoogle, loading } = useAuth()
  const navigate = useNavigate()
  const [isSigningIn, setIsSigningIn] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (user) {
      navigate(consumePostLoginPath())
    }
  }, [user, navigate])

  const handleGoogleLogin = async () => {
    try {
      setIsSigningIn(true)
      setError(null)
      await signInWithGoogle()
    } catch (error) {
      captureError(error as Error, { context: 'Google sign in' })
      setError(t('auth.signInError'))
      setIsSigningIn(false)
    }
  }

  const handleEmailAuthSuccess = () => {
    navigate(consumePostLoginPath())
  }

  const handleEmailAuthError = (errorMessage: string) => {
    setError(errorMessage)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="relative flex items-center justify-center min-h-screen overflow-hidden bg-background">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-primary/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

      <div className="relative z-10 w-full max-w-md px-4">
        <Card className="border-0 shadow-xl bg-card/95 backdrop-blur">
          <CardHeader className="space-y-4 pb-6">
            <div className="flex justify-center">
              <div className="w-24 h-24 flex items-center justify-center">
                <img src="/logo.svg" alt="Longbourn Club" className="w-20 h-20 object-contain" />
              </div>
            </div>
            <div className="space-y-2 text-center">
              <CardTitle className="text-3xl font-bold bg-gradient-to-r from-primary to-purple bg-clip-text text-transparent">
                {t('common.appName')}
              </CardTitle>
              <CardDescription className="text-base">
                {t('auth.yourPersonalLibrary')}
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {error && (
              <div className="p-4 bg-error/5 border border-error/20 rounded-lg">
                <p className="text-sm text-error flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                    />
                  </svg>
                  {error}
                </p>
              </div>
            )}

            {/* Email Authentication Form - Primary Method */}
            <EmailAuthForm onSuccess={handleEmailAuthSuccess} onError={handleEmailAuthError} />

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">ou</span>
              </div>
            </div>

            {/* Google Authentication - Secondary Option */}
            <Button
              onClick={handleGoogleLogin}
              disabled={isSigningIn}
              size="lg"
              variant="outline"
              className="w-full"
            >
              {isSigningIn ? (
                <LoadingSpinner size="sm" />
              ) : (
                <span className="flex items-center justify-center gap-3">
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span className="font-medium">{t('auth.signInWithGoogle')}</span>
                </span>
              )}
            </Button>

            <div className="text-center text-sm text-muted-foreground pt-4 space-y-1">
              <p>{t('auth.bySigningIn')}</p>
              <p>
                <a href="/privacy" className="underline hover:text-foreground transition-colors">
                  {t('auth.privacyPolicy')}
                </a>
                {' • '}
                <a href="/terms" className="underline hover:text-foreground transition-colors">
                  {t('auth.termsOfService')}
                </a>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Language Switcher - centered below card */}
        <div className="flex justify-center mt-6">
          <LanguageSwitcher variant="minimal" />
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-6">{t('auth.madeWithLove')}</p>
      </div>
    </div>
  )
}
